import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authedFetch } from '../api'

export interface Container {
  id: string
  container_name: string
  status: string
  current_task_id: string | null
  task_info: { label: string; link: string } | null
  retry_count: number
  error: string
  created_at: string
  stopped_at: string | null
}

export interface RecentAssessment {
  id: string
  status: string
  assessed_at: string | null
  verify_started_at: string | null
  cost: number
  failure_title: string
  failure_short_id: string
  failure_id: string
  agent_name: string | null
}

export interface RecentSummarization {
  id: string
  title: string
  short_id: string
  summarize_status: string
  summarize_error: string
}

interface ContainersState {
  items: Container[]
  loading: boolean
  error: string | null
  total: number
  activeCount: number
  maxContainers: number
  maxAssessments: number
  maxSummarizations: number
  assessingCount: number
  pendingAssessmentCount: number
  summarizingCount: number
  filterStatus: string
  recentAssessments: RecentAssessment[]
  recentSummarizations: RecentSummarization[]
}

const initialState: ContainersState = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  activeCount: 0,
  maxContainers: 10,
  maxAssessments: 5,
  maxSummarizations: 3,
  assessingCount: 0,
  pendingAssessmentCount: 0,
  summarizingCount: 0,
  filterStatus: '',
  recentAssessments: [],
  recentSummarizations: [],
}

export const fetchContainers = createAsyncThunk(
  'containers/fetchContainers',
  async (filters?: { status?: string }) => {
    const params = new URLSearchParams()
    if (filters?.status) params.set('status', filters.status)
    params.set('limit', '100')
    const response = await fetch(`/api/containers-api?${params.toString()}`)
    if (!response.ok) throw new Error('Failed to fetch containers')
    return response.json() as Promise<{
      items: Container[]
      total: number
      activeCount: number
      maxContainers: number
      maxAssessments: number
      maxSummarizations: number
      assessingCount: number
      pendingAssessmentCount: number
      summarizingCount: number
      recentAssessments: RecentAssessment[]
      recentSummarizations: RecentSummarization[]
    }>
  },
)

export const updateMaxContainers = createAsyncThunk(
  'containers/updateMaxContainers',
  async (maxContainers: number) => {
    const response = await authedFetch('/api/containers-api', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ max_containers: maxContainers }),
    })
    if (!response.ok) throw new Error('Failed to update max containers')
    return maxContainers
  },
)

export const updateMaxAssessments = createAsyncThunk(
  'containers/updateMaxAssessments',
  async (maxAssessments: number) => {
    const response = await authedFetch('/api/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ max_assessments: String(maxAssessments) }),
    })
    if (!response.ok) throw new Error('Failed to update max assessments')
    return maxAssessments
  },
)

export const updateMaxSummarizations = createAsyncThunk(
  'containers/updateMaxSummarizations',
  async (maxSummarizations: number) => {
    const response = await authedFetch('/api/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ max_summarizations: String(maxSummarizations) }),
    })
    if (!response.ok) throw new Error('Failed to update max summarizations')
    return maxSummarizations
  },
)

export const retryContainer = createAsyncThunk(
  'containers/retryContainer',
  async (containerId: string) => {
    const response = await authedFetch('/api/containers-api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ container_id: containerId, action: 'retry' }),
    })
    if (!response.ok) throw new Error('Failed to retry container')
    return containerId
  },
)

const containersSlice = createSlice({
  name: 'containers',
  initialState,
  reducers: {
    setFilterStatus(state, action) {
      state.filterStatus = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContainers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchContainers.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.total = action.payload.total
        state.activeCount = action.payload.activeCount
        state.maxContainers = action.payload.maxContainers
        state.maxAssessments = action.payload.maxAssessments
        state.maxSummarizations = action.payload.maxSummarizations
        state.assessingCount = action.payload.assessingCount
        state.pendingAssessmentCount = action.payload.pendingAssessmentCount
        state.summarizingCount = action.payload.summarizingCount
        state.recentAssessments = action.payload.recentAssessments || []
        state.recentSummarizations = action.payload.recentSummarizations || []
      })
      .addCase(fetchContainers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch containers'
      })
      .addCase(updateMaxContainers.fulfilled, (state, action) => {
        state.maxContainers = action.payload
      })
      .addCase(updateMaxAssessments.fulfilled, (state, action) => {
        state.maxAssessments = action.payload
      })
      .addCase(updateMaxSummarizations.fulfilled, (state, action) => {
        state.maxSummarizations = action.payload
      })
  },
})

export const { setFilterStatus } = containersSlice.actions
export default containersSlice.reducer
